import { useRef } from "react";
import { Paperclip, X } from "lucide-react";

const OUT_CATEGORIES = [
  "💳 Business expense",
  "🏠 Personal / owner draw",
  "🔁 Transfer between accounts",
  "🤝 Loan (given or received)",
  "↩️ Refund",
  "❓ Other (see note)",
];
const IN_CATEGORIES = [
  "💰 Income / sale",
  "🤝 Loan (given or received)",
  "🏠 Personal / owner draw",
  "🔁 Transfer between accounts",
  "↩️ Refund",
  "❓ Other (see note)",
];

const fmtMoney = (amt) => {
  const n = Math.abs(Number(amt) || 0);
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const fmtDate = (d) => {
  if (!d) return "";
  const dt = new Date(d + "T00:00:00");
  return isNaN(dt) ? d : dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const readFile = (file) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      resolve({
        filename: file.name,
        contentType: file.type || "application/octet-stream",
        base64: result.split(",")[1] || "",
        preview: file.type.startsWith("image/") ? result : null,
      });
    };
    reader.readAsDataURL(file);
  });

const Chip = ({ label, selected, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className="text-[13px] font-medium rounded-full px-3.5 py-2 border transition-colors active:scale-95"
    style={
      selected
        ? { backgroundColor: "#16a34a", color: "#fff", borderColor: "#16a34a" }
        : { backgroundColor: "#f9fafb", color: "#374151", borderColor: "#e5e7eb" }
    }
  >
    {label}
  </button>
);

export default function QuestionCard({ item, index, answer, onChange }) {
  const fileRef = useRef(null);
  const isOut = (item.direction || "").toLowerCase() === "money out";
  const categories = isOut ? OUT_CATEGORIES : IN_CATEGORIES;
  const completed = !!answer.category;

  const addFiles = async (fileList) => {
    const arr = Array.from(fileList || []);
    if (!arr.length) return;
    const read = await Promise.all(arr.map(readFile));
    onChange({ files: [...(answer.files || []), ...read] });
  };

  return (
    <div
      className="rounded-2xl bg-white p-4 shadow-sm transition-colors"
      style={{ border: completed ? "2px solid #16a34a" : "1px solid #e5e7eb" }}
      data-testid={`q-card-${index}`}
    >
      {/* read-only context */}
      <div className="flex items-start justify-between gap-3">
        <p className="font-semibold text-[14px] text-gray-900 leading-snug flex-1">
          {item.memo || item.name || "Transaction"}
        </p>
        <div className="text-right shrink-0">
          <div
            className="font-bold text-[15px]"
            style={{ color: isOut ? "#b4432f" : "#127a4b" }}
            data-testid={`q-amount-${index}`}
          >
            {fmtMoney(item.amount)}
          </div>
          <div className="text-[10px] font-semibold tracking-wide text-gray-400">
            {isOut ? "MONEY OUT" : "MONEY IN"}
          </div>
        </div>
      </div>
      <p className="text-[12px] text-gray-500 mt-1">
        {fmtDate(item.date)}
        {item.account ? ` · ${item.account}` : ""}
        {item.vendor ? ` · ${item.vendor}` : ""}
      </p>

      {/* category */}
      <p className="mt-4 text-[13px] font-semibold text-gray-800">
        What was this for?
      </p>
      <div className="flex flex-wrap gap-2 mt-2">
        {categories.map((c, ci) => (
          <Chip
            key={c}
            label={c}
            selected={answer.category === c}
            onClick={() => onChange({ category: answer.category === c ? undefined : c })}
            testid={`q-${index}-cat-${ci}`}
          />
        ))}
      </div>

      {/* purpose */}
      <div className="mt-4">
        <label className="text-[13px] font-semibold text-gray-800">
          Any detail? <span className="font-normal text-gray-400">(optional)</span>
        </label>
        <textarea
          value={answer.purpose || ""}
          onChange={(e) => onChange({ purpose: e.target.value })}
          rows={2}
          placeholder="Add a note…"
          data-testid={`q-${index}-purpose`}
          className="mt-1.5 w-full px-3 py-2 rounded-xl border border-gray-200 bg-gray-50 text-[14px] outline-none focus:border-[#16a34a] resize-none"
        />
      </div>

      {/* attachment */}
      <div className="mt-4">
        <input
          ref={fileRef}
          type="file"
          accept="image/*,application/pdf"
          multiple
          className="hidden"
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = "";
          }}
          data-testid={`q-${index}-file-input`}
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          data-testid={`q-${index}-attach-btn`}
          className="flex items-center gap-2 text-[13px] font-semibold text-[#16a34a] border border-dashed border-green-300 rounded-xl px-4 py-2.5 transition-colors active:scale-95"
        >
          <Paperclip className="w-4 h-4" /> Attach receipt
        </button>
        {(answer.files || []).length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3" data-testid={`q-${index}-thumbs`}>
            {(answer.files || []).map((f, i) => (
              <div key={i} className="relative">
                {f.preview ? (
                  <img src={f.preview} alt={f.filename} className="w-14 h-14 object-cover rounded-lg border border-gray-200" />
                ) : (
                  <div className="w-14 h-14 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center text-[9px] text-gray-500">PDF</div>
                )}
                <button
                  type="button"
                  onClick={() => onChange({ files: (answer.files || []).filter((_, idx) => idx !== i) })}
                  data-testid={`q-${index}-remove-file-${i}`}
                  className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-gray-700 text-white flex items-center justify-center"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
